// Client-side rotation for order attachments (images + PDFs). The current bytes
// are fetched from the attachment's signed URL, rotated in the browser, and
// returned as a base64 `data:` URL ready to PATCH back over the same object —
// so a crooked scan can be straightened without leaving the order page. Doing
// it client-side keeps the backend free of image/PDF native deps (the Vercel
// Python build is deliberately kept lean).

import { prepareImageUpload, type PreparedUpload } from './imageUpload';
import { apiFetch } from './apiFetch';

const PDF_MIME = 'application/pdf';
// canvas.toBlob only emits these; anything else is re-encoded to JPEG.
const CANVAS_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// btoa chokes on large strings if fed all at once via apply; chunk the bytes.
const uint8ToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + chunk) as unknown as number[],
    );
  }
  return btoa(binary);
};

const normalizeDegrees = (deg: number): number => ((Math.round(deg / 90) * 90) % 360 + 360) % 360;

async function rotateImage(
  blob: Blob,
  name: string,
  type: string,
  degrees: number,
): Promise<PreparedUpload> {
  // `from-image` so EXIF-oriented phone photos rotate relative to what the user
  // sees in the viewer (which applies EXIF), not the raw sensor orientation.
  const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
  const rot = normalizeDegrees(degrees);
  const swap = rot === 90 || rot === 270;
  const canvas = document.createElement('canvas');
  canvas.width = swap ? bitmap.height : bitmap.width;
  canvas.height = swap ? bitmap.width : bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rot * Math.PI) / 180);
  ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);
  bitmap.close?.();
  const outType = CANVAS_IMAGE_TYPES.includes(type) ? type : 'image/jpeg';
  const out: Blob = await new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas export failed'))),
      outType,
      0.92,
    ),
  );
  // Route through prepareImageUpload so a large rotated image still lands under
  // the platform body cap (and keeps its format when already small).
  return prepareImageUpload(new File([out], name, { type: outType }));
}

async function rotatePdf(blob: Blob, name: string, degrees: number): Promise<PreparedUpload> {
  // Dynamic import keeps pdf-lib (~hundreds of KB) out of the main bundle until
  // someone actually rotates a PDF.
  const { PDFDocument, degrees: pdfDegrees } = await import('pdf-lib');
  const doc = await PDFDocument.load(await blob.arrayBuffer());
  const delta = normalizeDegrees(degrees);
  for (const page of doc.getPages()) {
    const current = page.getRotation().angle; // existing per-page rotation, deg
    page.setRotation(pdfDegrees((current + delta) % 360));
  }
  const bytes = await doc.save();
  return {
    dataUrl: `data:${PDF_MIME};base64,${uint8ToBase64(bytes)}`,
    fileType: PDF_MIME,
    name,
  };
}

/**
 * Fetch an attachment's current bytes and return a rotated copy (clockwise by
 * `degrees`, a multiple of 90) as an upload-ready data URL. Fetching the bytes
 * ourselves — rather than reusing the viewer's cross-origin <img> — sidesteps
 * canvas tainting; Supabase signed URLs send permissive CORS so the read works.
 */
export async function rotateAttachment(opts: {
  url: string;
  name: string;
  fileType: string | null;
  degrees: number;
}): Promise<PreparedUpload> {
  const { url, name, fileType, degrees } = opts;
  // apiFetch passes absolute URLs straight through (no backend token attached),
  // so it's a plain fetch for the Supabase signed URL — and keeps the
  // no-direct-fetch lint rule happy.
  const resp = await apiFetch(url);
  if (!resp.ok) throw new Error(`Could not load file (HTTP ${resp.status})`);
  const blob = await resp.blob();
  const type = (fileType || blob.type || '').toLowerCase();
  if (type === PDF_MIME || /\.pdf$/i.test(name)) return rotatePdf(blob, name, degrees);
  return rotateImage(blob, name, type, degrees);
}
